import { useState, useEffect } from 'react';

/**
 * Custom hook for debouncing values
 * @param {any} value - The value to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {any} - Debounced value
 */
export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Custom hook for debounced search functionality
 * @param {Function} searchFunction - Function to call with search term
 * @param {number} delay - Delay in milliseconds (default: 300)
 * @returns {Object} - Search state and functions
 */
export function useDebouncedSearch(searchFunction, delay = 300) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, delay);

  useEffect(() => {
    if (debouncedSearchTerm) {
      setIsSearching(true);
      searchFunction(debouncedSearchTerm).finally(() => {
        setIsSearching(false);
      });
    } else {
      searchFunction(''); // Clear search
    }
  }, [debouncedSearchTerm, searchFunction]);

  return {
    searchTerm,
    setSearchTerm,
    isSearching,
    debouncedSearchTerm
  };
}

export default useDebounce;